import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ChangeDetectorRef,
  HostListener
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TourService, TourId } from '../../core/services/tour.service';
import { TOUR_DEFINITIONS, TourStep, TourDefinition } from '../../core/tour/tour.definitions';

@Component({
  selector: 'app-tour',
  templateUrl: './tour.component.html',
  styleUrls: ['./tour.component.css'],
  standalone: false
})
export class TourComponent implements OnInit, OnDestroy {
  visible = false;
  currentStep = 0;
  centered = true;
  cardLeft = '';
  cardTop = '';
  dontShowAgain = true;
  tourName = '';
  private tourId: TourId | null = null;

  spotlight = { left: '0px', top: '0px', width: '0px', height: '0px', visible: false };

  private destroy$ = new Subject<void>();
  private readonly CARD_MARGIN = 14;
  private readonly CARD_WIDTH = 360;

  constructor(
    private tourService: TourService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.tourService.onOpenRequest.pipe(takeUntil(this.destroy$)).subscribe(({ tourId }) => {
      this.startTour(tourId);
    });
    this.tourService.onClose.pipe(takeUntil(this.destroy$)).subscribe(() => this.close(false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearHighlight();
  }

  get definition(): TourDefinition | null {
    return this.tourId ? TOUR_DEFINITIONS[this.tourId] : null;
  }

  get steps(): TourStep[] {
    return this.definition?.steps ?? [];
  }

  get step(): TourStep {
    return this.steps[this.currentStep] ?? this.steps[0];
  }

  get nextLabel(): string {
    if (this.currentStep === 0) return 'Comenzar';
    if (this.currentStep >= this.steps.length - 1) return 'Finalizar';
    return 'Siguiente';
  }

  get progressLabel(): string {
    return `Paso ${this.currentStep + 1} de ${this.steps.length}`;
  }

  next(): void {
    if (this.currentStep >= this.steps.length - 1) {
      this.close(true);
      return;
    }
    this.currentStep++;
    this.afterStepChange();
  }

  prev(): void {
    if (this.currentStep <= 0) return;
    this.currentStep--;
    this.afterStepChange();
  }

  skip(): void {
    this.close(true);
  }

  close(markSeen: boolean): void {
    if (markSeen && this.tourId && this.dontShowAgain) {
      this.tourService.markSeen(this.tourId);
    }
    this.visible = false;
    this.clearHighlight();
    this.spotlight.visible = false;
    this.tourId = null;
    this.releaseShellDrawer();
  }

  /** Quita el drawer que el tour pudo forzar abrir en móvil. */
  private releaseShellDrawer(): void {
    const shell = this.document.querySelector('app-app-shell') as HTMLElement | null;
    shell?.classList.remove('drawer-open');
    this.document.body.classList.remove('drawer-open');
  }

  onOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('tour-overlay')) {
      this.close(true);
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (!this.visible) return;
    this.updatePosition();
    this.updateHighlight();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) this.close(true);
  }

  private startTour(tourId: TourId): void {
    this.tourId = tourId;
    this.tourName = TOUR_DEFINITIONS[tourId].name;
    this.currentStep = 0;
    this.dontShowAgain = true;
    this.visible = true;
    this.cdr.detectChanges();
    this.afterStepChange();
  }

  private afterStepChange(): void {
    this.prepareChromeForStep();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.updatePosition();
      this.updateHighlight();
      this.cdr.detectChanges();
    }, 80);
  }

  private prepareChromeForStep(): void {
    const step = this.step;
    const shell = this.document.querySelector('app-app-shell') as HTMLElement | null;
    if (!shell) return;

    if (step.ensureSidebarOpen) {
      shell.classList.add('drawer-open');
      this.document.body.classList.add('drawer-open');
    }

    // Expande menús vía clases/aria si los botones existen
    if (step.ensureFirmarMenuOpen) {
      const btn = this.document.querySelector('.firmar-verificar-btn') as HTMLElement | null;
      const list = this.document.getElementById('firmar-verificar-list');
      if (btn && list && list.hasAttribute('hidden')) {
        btn.click();
      }
    }
    if (step.ensureAdminMenuOpen) {
      const btn = this.document.querySelector('.admin-btn') as HTMLElement | null;
      const list = this.document.getElementById('admin-sub');
      if (btn && list && list.hasAttribute('hidden')) {
        btn.click();
      }
    }
  }

  private updatePosition(): void {
    const step = this.step;
    if (!step?.targetId) {
      this.centered = true;
      this.spotlight.visible = false;
      this.cardLeft = '';
      this.cardTop = '';
      return;
    }

    const target = this.document.getElementById(step.targetId);
    if (!target) {
      this.centered = true;
      this.spotlight.visible = false;
      return;
    }

    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    const rect = target.getBoundingClientRect();
    const pad = 6;
    this.spotlight = {
      left: `${Math.max(8, rect.left - pad)}px`,
      top: `${Math.max(8, rect.top - pad)}px`,
      width: `${rect.width + pad * 2}px`,
      height: `${rect.height + pad * 2}px`,
      visible: true
    };

    const vw = this.document.documentElement.clientWidth;
    const vh = window.innerHeight;
    let left = rect.right + this.CARD_MARGIN;
    let top = rect.top;

    if (left + this.CARD_WIDTH > vw - 16) {
      left = Math.max(16, rect.left - this.CARD_WIDTH - this.CARD_MARGIN);
    }
    if (left < 16) left = 16;
    if (top < 72) top = 72;
    if (top + 340 > vh - 16) top = Math.max(72, vh - 340 - 16);

    this.centered = false;
    this.cardLeft = `${left}px`;
    this.cardTop = `${top}px`;
  }

  private updateHighlight(): void {
    this.clearHighlight();
    const step = this.step;
    if (step?.targetId) {
      const el = this.document.getElementById(step.targetId);
      if (el) el.classList.add('tour-highlight');
    }
  }

  private clearHighlight(): void {
    this.document.querySelectorAll('.tour-highlight').forEach((el) => el.classList.remove('tour-highlight'));
  }
}
